<template>
	<div ref="zoneRef" class="promo-step py-2">
		<template v-if="snapshot">
			<PromoCampaignPage v-if="compact && selectedCampaign" :campaign="selectedCampaign" @back="closeCampaign" />
			<template v-else>
				<PromoStepHeader class="mb-4" />
				<v-alert v-if="!snapshot.campaigns.length" type="info" variant="tonal" density="compact">{{ t('NoPromoFound') }}</v-alert>
				<div v-if="compact" class="d-flex flex-column ga-3">
					<PromoCampaignListItem v-for="campaign in snapshot.campaigns" :key="campaign.id" :campaign="campaign" @open="openCampaign" />
				</div>
				<PromoCampaignPanel v-for="campaign in snapshot.campaigns" v-else :key="campaign.id" :campaign="campaign" class="mb-4" />
			</template>
		</template>
		<PromoConfirmDialog />
		<PromoPickerDialog />
		<v-snackbar v-model="notice.visible" :color="notice.color" :attach="overlayTarget" timeout="4000" location="bottom">{{ notice.text }}</v-snackbar>
	</div>
</template>

<script setup>
import { computed, inject, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { providePromoStep } from '@/composables/usePromoStep';
import { isCompactPromoLayout } from '@/utils/domain/promo/promoPhase';
import PromoStepHeader from './PromoStepHeader.vue';
import PromoCampaignPanel from './PromoCampaignPanel.vue';
import PromoCampaignListItem from './PromoCampaignListItem.vue';
import PromoCampaignPage from './PromoCampaignPage.vue';
import PromoConfirmDialog from './PromoConfirmDialog.vue';
import PromoPickerDialog from './PromoPickerDialog.vue';

const { snapshot, notice, compact, attach, receive, ops, t } = providePromoStep();
const overlayTarget = inject('overlayTarget', null);
const zoneRef = ref(null);
const selectedId = ref(null);
let mountTarget = null;
let frameWindow = null;

const selectedCampaign = computed(() => (selectedId.value === null ? null : snapshot.value?.campaigns.find((c) => c.id === selectedId.value) || null));

const onContext = (event) => receive(event?.detail);
const onResize = () => {
	compact.value = isCompactPromoLayout(frameWindow?.innerWidth);
};

async function openCampaign(campaignId) {
	selectedId.value = campaignId;
	ops.run({ type: 'enterCampaign' });
	await nextTick();
	zoneRef.value?.scrollIntoView({ block: 'start' });
}

function closeCampaign() {
	selectedId.value = null;
	ops.run({ type: 'leaveCampaign' });
}

watch(compact, (value) => {
	if (!value && selectedId.value !== null) closeCampaign();
});

watch(selectedCampaign, (campaign) => {
	if (!campaign && selectedId.value !== null) closeCampaign();
});

onMounted(() => {
	mountTarget = zoneRef.value?.parentElement;
	frameWindow = zoneRef.value?.ownerDocument?.defaultView || null;
	onResize();
	frameWindow?.addEventListener('resize', onResize);
	attach(mountTarget);
	mountTarget?.addEventListener('vue:promo-step-context', onContext);
	if (mountTarget?._pendingPromoStepContext) receive(mountTarget._pendingPromoStepContext);
});

onBeforeUnmount(() => {
	if (selectedId.value !== null) ops.run({ type: 'leaveCampaign' });
	frameWindow?.removeEventListener('resize', onResize);
	mountTarget?.removeEventListener('vue:promo-step-context', onContext);
	attach(null);
	mountTarget = null;
	frameWindow = null;
});
</script>
